/**
 * Messages slice (PX-W6.2) — the org thread: list, post, edit own, delete own.
 * Every mutation MERGES the response row by id (append for a post, overwrite
 * for an edit, remove for a delete) — never a bare replace.
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { selectActiveOrgId, type SessionState } from '@/features/auth/slices/sessionSlice'
import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import * as messagesHttp from './messages.http'
import { ORG_THREAD_CONTEXT_TYPE, type ApiMessage } from './types'

/**
 * Preserve the canonical ApiError: the shared transport's interceptor already
 * maps AxiosErrors to it, so re-mapping through `toApiError` would degrade a
 * real 403 to the network fallback. Only strangers go through the mapper.
 */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

const orgKey = (getState: () => unknown): string | null =>
  selectActiveOrgId(getState() as { session: SessionState })

interface OrgKeyed<T> {
  orgId: string | null
  value: T
}

/** Merge one row by id; a row with no id appends (the server always sends one,
 * but a malformed row must not crash the thread). */
const upsertMessage = (state: MessagesState, row: ApiMessage): void => {
  if (row.id === undefined) return
  const existing = state.items.findIndex((item) => item.id === row.id)
  if (existing >= 0) state.items[existing] = { ...state.items[existing], ...row }
  else state.items.push(row)
}

/**
 * The org thread's first page. The LIST CONTRACT: the server 400s blank
 * `contextType`/`contextId` (route defaults are empty strings), so the tuple
 * is non-negotiable — the org thread is tagged `{ orgId, <activeOrgId> }`.
 */
export const fetchMessages = createAsyncThunk<
  OrgKeyed<{ rows: ApiMessage[]; nextCursor: string | null }>,
  { limit?: number } | undefined,
  { rejectValue: ApiError }
>(
  'messages/fetchMessages',
  async (params, { getState, rejectWithValue }) => {
    const orgId = orgKey(getState)
    try {
      const page = await messagesHttp.messagesHttp.list({
        contextType: ORG_THREAD_CONTEXT_TYPE,
        contextId: orgId ?? '',
        limit: params?.limit ?? 50
      })
      return {
        orgId,
        value: { rows: page.items ?? [], nextCursor: page.nextCursor ?? null }
      }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { messages: MessagesState }).messages.cache, orgKey(getState))
  }
)

/**
 * Post to the org thread. The CREATE CONTRACT: the body's `contextIds` must
 * be NON-EMPTY for every subtype (`message.contextIds.required`) — the
 * OPPOSITE of the activity contract. The caller supplies only the content;
 * the org ref is stamped here, where the active org is known.
 */
export const postMessage = createAsyncThunk<
  ApiMessage,
  { content: string },
  { rejectValue: ApiError }
>('messages/postMessage', async ({ content }, { getState, rejectWithValue }) => {
  const orgId = orgKey(getState)
  try {
    return await messagesHttp.messagesHttp.create({
      type: 'USER_MESSAGE',
      contextIds: [{ type: ORG_THREAD_CONTEXT_TYPE, id: orgId ?? '' }],
      content
    })
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

/** The next page under the current cursor (appends, never replaces). */
export const fetchMoreMessages = createAsyncThunk<
  { rows: ApiMessage[]; nextCursor: string | null },
  void,
  { rejectValue: ApiError }
>('messages/fetchMoreMessages', async (_, { getState, rejectWithValue }) => {
  const { messages } = getState() as { messages: MessagesState }
  try {
    const page = await messagesHttp.messagesHttp.list({
      contextType: ORG_THREAD_CONTEXT_TYPE,
      contextId: orgKey(getState) ?? '',
      afterCursor: messages.nextCursor ?? undefined,
      limit: 50
    })
    return { rows: page.items ?? [], nextCursor: page.nextCursor ?? null }
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export const editMessage = createAsyncThunk<
  ApiMessage,
  { id: string; content: string },
  { rejectValue: ApiError }
>('messages/editMessage', async ({ id, content }, { rejectWithValue }) => {
  try {
    return await messagesHttp.messagesHttp.patch(id, { content })
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export const deleteMessage = createAsyncThunk<string, string, { rejectValue: ApiError }>(
  'messages/deleteMessage',
  async (id, { rejectWithValue }) => {
    try {
      await messagesHttp.messagesHttp.remove(id)
      return id
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  }
)

export interface MessagesState {
  items: ApiMessage[]
  nextCursor: string | null
  cache: FetchCache
  error: ApiError | null
  mutationError: ApiError | null
}

const initialState: MessagesState = {
  items: [],
  nextCursor: null,
  cache: initialFetchCache,
  error: null,
  mutationError: null
}

export const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    mutationErrorDismissed(state) {
      state.mutationError = null
    },
    retryClicked(state) {
      invalidate(state.cache)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMessages.pending, (state) => {
        fetchStarted(state.cache)
        state.error = null
      })
      .addCase(
        fetchMessages.fulfilled,
        (
          state,
          action: PayloadAction<OrgKeyed<{ rows: ApiMessage[]; nextCursor: string | null }>>
        ) => {
          fetchSucceeded(state.cache, action.payload.orgId)
          state.items = action.payload.value.rows
          state.nextCursor = action.payload.value.nextCursor
        }
      )
      .addCase(fetchMessages.rejected, (state, action) => {
        fetchFailed(state.cache)
        state.error = action.payload ?? null
      })
      .addCase(fetchMoreMessages.fulfilled, (state, action) => {
        const seen = new Set(state.items.map((row) => row.id ?? ''))
        for (const row of action.payload.rows) {
          if (row.id !== undefined && seen.has(row.id)) continue
          seen.add(row.id ?? '')
          state.items.push(row)
        }
        state.nextCursor = action.payload.nextCursor
      })
      .addCase(postMessage.pending, (state) => {
        state.mutationError = null
      })
      .addCase(postMessage.fulfilled, (state, action: PayloadAction<ApiMessage>) => {
        upsertMessage(state, action.payload)
      })
      .addCase(postMessage.rejected, (state, action) => {
        state.mutationError = action.payload ?? null
      })
      .addCase(editMessage.pending, (state) => {
        state.mutationError = null
      })
      .addCase(editMessage.fulfilled, (state, action: PayloadAction<ApiMessage>) => {
        upsertMessage(state, action.payload)
      })
      .addCase(editMessage.rejected, (state, action) => {
        state.mutationError = action.payload ?? null
      })
      .addCase(deleteMessage.pending, (state) => {
        state.mutationError = null
      })
      .addCase(deleteMessage.fulfilled, (state, action: PayloadAction<string>) => {
        state.items = state.items.filter((row) => row.id !== action.payload)
      })
      .addCase(deleteMessage.rejected, (state, action) => {
        state.mutationError = action.payload ?? null
      })
  }
})

export const { mutationErrorDismissed, retryClicked: messagesRetryClicked } = messagesSlice.actions
